import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, CreditCard } from "lucide-react";
import { AfterpayToday } from "@/components/booking/PayLaterCalculator";

const STICKER_TONES = {
  limited: "bg-yellow-400 text-black",
  full: "bg-red-600 text-white",
  open: "bg-emerald-400 text-black",
};

export const SpotsSticker = ({ sticker, slug, className = "" }) => {
  if (!sticker) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg ${STICKER_TONES[sticker.tone]} ${className}`}
      data-testid={`mentor-${slug}-sticker`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      {sticker.text}
    </span>
  );
};

const MentorPhoto = ({ m, flip }) => (
  <div className={`relative lg:col-span-5 min-h-[420px] lg:min-h-full overflow-hidden ${flip ? "lg:order-last" : ""}`}>
    <img
      src={m.photo}
      alt={m.name}
      loading="lazy"
      className={`absolute inset-0 w-full h-full object-cover ${m.photoPos || "object-center"} transition-transform duration-700 group-hover:scale-105`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-dream-bg via-dream-bg/30 to-transparent" />
    <div className="absolute top-5 left-5 right-5 flex flex-wrap items-start justify-between gap-2">
      <span className={`px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${m.accent}`}>{m.badge}</span>
      <SpotsSticker sticker={m.sticker} slug={m.slug} className="rotate-3" />
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="font-heading text-4xl md:text-5xl font-bold text-white" data-testid={`mentor-${m.slug}-name`}>{m.name}</h3>
        {m.realName && <span className="text-white/50 text-sm">({m.realName})</span>}
      </div>
      <p className={`text-transparent bg-clip-text bg-gradient-to-r ${m.accent} font-medium text-sm mt-1`}>{m.role}</p>
    </div>
  </div>
);

const FactGrid = ({ m }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    {m.facts.map(({ icon: Icon, main, sub }) => (
      <div key={sub} className="rounded-xl p-3 text-center bg-white/[0.03] border border-white/5">
        <Icon className={`w-5 h-5 mx-auto mb-1.5 ${m.iconColor}`} />
        <p className="text-white font-semibold text-sm">{main}</p>
        <p className="text-white/40 text-[11px] uppercase tracking-wider">{sub}</p>
      </div>
    ))}
  </div>
);

const PriceBar = ({ m }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-dream-bg/70 border border-white/10">
    <div>
      <p className="text-white/50 text-xs uppercase tracking-widest">{m.priceLabel}</p>
      <p className="font-heading text-4xl md:text-5xl font-bold text-white" data-testid={`mentor-${m.slug}-price`}>{m.price}</p>
      <p className="text-white/50 text-sm">{m.priceNote}</p>
      <p className="flex items-center gap-1.5 text-green-400 text-xs mt-1"><CreditCard className="w-3.5 h-3.5" /> Afterpay &amp; Zip Pay · 4 × 0% interest</p>
      <AfterpayToday price={m.price} className="mt-1" testId={`mentor-${m.slug}-afterpay`} />
    </div>
    <Link to={m.cta}>
      <Button className={`${m.btn} text-white rounded-full px-7 py-6 font-bold w-full sm:w-auto`} data-testid={m.testId}>
        {m.ctaLabel}
        <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </Link>
  </div>
);

export const MentorTile = ({ m, idx }) => (
  <motion.article
    id={`mentor-${m.slug}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5 }}
    className={`group relative grid lg:grid-cols-12 overflow-hidden rounded-3xl border ${m.border} bg-dream-surface/50 backdrop-blur-sm scroll-mt-28`}
    data-testid={`instructor-${m.slug}`}
  >
    <MentorPhoto m={m} flip={idx % 2 === 1} />
    <div className="lg:col-span-7 p-6 md:p-10 space-y-6">
      <div>
        <p className={`font-mono text-xs tracking-widest uppercase mb-2 ${m.iconColor}`}>{m.tagline}</p>
        <h4 className="font-heading text-2xl md:text-3xl font-bold text-white" data-testid={`mentor-${m.slug}-course`}>{m.course}</h4>
        <p className="text-white/65 text-sm leading-relaxed mt-3">{m.bio}</p>
      </div>
      <FactGrid m={m} />
      <div>
        <p className="text-white/50 text-xs uppercase tracking-widest mb-3">{m.listTitle}</p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {m.included.map((item) => (
            <li key={item} className="flex items-start gap-2 text-white/80 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
        {m.detailsLink && (
          <a href={m.detailsLink} className={`inline-flex items-center gap-1 text-sm mt-4 hover:underline ${m.iconColor}`} data-testid={`mentor-${m.slug}-details-link`}>
            See the full week-by-week breakdown <ArrowRight className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      <PriceBar m={m} />
    </div>
  </motion.article>
);
